import {useAuth} from "@/lib/auth";
import FormRequirements from "@/components/FormRequirements";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Eye, Plus } from "lucide-react";
import { SubmissionViewerDialog, FormFillerDialog } from "@/pages/dashboard/Forms";

export const JobForms = ({ jobId, compactRequirements = false }) => {
  const {user}=useAuth();const office=["admin","office","super_admin"].includes(user?.role);
  const [subs, setSubs] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [viewingId, setViewingId] = useState(null);
  const [filling, setFilling] = useState(null);

  const load = () => {
    api.get(`/form_submissions?job_id=${jobId}`)
      .then((r) => setSubs(r.data))
      .catch(() => setSubs([]));
  };
  useEffect(() => {
    load();
    window.addEventListener("forms-changed",load);
    api.get("/form_templates")
      .then((r) => setTemplates((r.data || []).filter((t) => t.active && t.availability?.manual !== false && t.availability?.job !== false && ["job", "general", "safety"].includes(t.category || "general"))))
      .catch(() => setTemplates([]));
    return ()=>window.removeEventListener("forms-changed",load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const jobForms = templates.filter((t) => t.category === "job" && (!(t.job_ids || []).length || t.job_ids.includes(jobId)));
  const otherForms = templates.filter((t) => t.category !== "job");

  return (
    <section className="hard-card p-5" data-testid="job-forms-panel">
      <FormRequirements compact={compactRequirements} surface="job" jobId={jobId} title="Assigned job forms" officeControls={office} />
      <div className="label-tag mb-3 flex items-center gap-1.5">
        <ClipboardCheck className="w-3 h-3" /> Forms & permits on this job
        {subs && subs.length > 0 && (
          <span className="ml-1 bg-[#09090B] text-[#FF5F15] font-mono px-1.5 py-0.5 text-[9px]">{subs.length}</span>
        )}
      </div>
      {(jobForms.length > 0 || otherForms.length > 0) && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="job-forms-fill-row">
          {[...jobForms, ...otherForms].map((t) => (
            <button key={t.id} onClick={() => setFilling(t)}
              className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border px-2 py-1 rounded-sm transition-colors ${t.category === "job" ? "border-[#FF5F15] text-[#FF5F15] hover:bg-[#FF5F15] hover:text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}
              data-testid={`job-form-fill-${t.id}`}>
              <Plus className="w-3 h-3" /> {t.name}
            </button>
          ))}
        </div>
      )}
      {!subs ? (
        <div className="text-xs text-zinc-500 font-mono">Loading…</div>
      ) : subs.length === 0 ? (
        <p className="text-xs text-zinc-500">
          No form submissions on this job yet. Fill one above, or crew can assign a custom
          form to this job when filling it out — it'll show here and in the completion pack.
        </p>
      ) : (
        <div className="divide-y divide-zinc-200">
          {subs.map((s) => (
            <div key={s.id} className="py-2.5 flex items-center gap-3" data-testid={`job-form-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{s.template_name}{s.review_required&&<span className="ml-2 text-xs text-orange-700 dark:text-orange-300">{s.reviewed_at?"Reviewed":"Review required"}</span>}</div>
                <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                  {s.user_name} · {new Date(s.created_at).toLocaleString("en-NZ")}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setViewingId(s.id)}
                      className="rounded-sm text-xs font-bold uppercase tracking-wider shrink-0"
                      data-testid={`job-form-view-${s.id}`}>
                <Eye className="w-3.5 h-3.5 mr-1" /> View
              </Button>
            </div>
          ))}
        </div>
      )}
      <SubmissionViewerDialog submissionId={viewingId} onClose={() => setViewingId(null)} />
      <FormFillerDialog template={filling} defaultJobId={jobId} lockJob
        onClose={() => setFilling(null)}
        onSubmitted={() => { setFilling(null); load(); }} />
    </section>
  );
};
